import classNames from "classnames";
import React, { useMemo } from "react";

import { OutlinedButton } from "common/DS/Buttons";
import { Subtitle2 } from "common/DS/Text";
import COLOR from "lib/colors";
import { useIsBreakpoint } from "lib/hooks/window";
import { preventDefault } from "lib/util";

import QuestionButtonGroup from "./QuestionButtonGroup";
import { QuestionAnswerShape, SelectOneProps } from "./types";

const getAnswerText = (ans: QuestionAnswerShape, isSplit: boolean) =>
    isSplit ? `${ans.display_number || ans.value} - ${ans.display_text}` : ans.display_text;

const AnswerText = ({ text, className }: { text: string; className?: string }) => (
    <Subtitle2 className={classNames("text-bold", className)} color={COLOR["navy-medium"]}>
        {text}
    </Subtitle2>
);

const SelectOneENPS = ({
    answer,
    handleSelect,
    handleSkip,
    selectedAnswer,
    response,
    showFollowUp,
    isLastQuestion,
    isRequired,
}: SelectOneProps) => {
    const selectedResponse = typeof response === "string" ? response : response?.response;
    const { breakpointXS, breakpointSM } = useIsBreakpoint();
    const splitAnswers = breakpointXS || breakpointSM;
    const splitIdx = Math.round(answer.length / 2);

    const answerGroups = useMemo(
        () => (splitAnswers ? [answer.slice(0, splitIdx), answer.slice(splitIdx)] : [answer]),
        [answer, splitAnswers],
    );
    const textLeft = getAnswerText(answer[0], splitAnswers);
    const textRight = getAnswerText(answer.slice(-1)[0], splitAnswers);

    return (
        <>
            {!selectedAnswer && (
                <div className="check-in__enps-answer-container">
                    {answerGroups.map((ag, agIdx) => (
                        <div key={`ag-${agIdx}`}>
                            {/* Show textLeft on top if split and in the first group */}
                            {textLeft && agIdx === 0 && splitAnswers && (
                                <AnswerText text={textLeft} />
                            )}
                            <div className="check-in__enps-icon-group">
                                {ag.map((a) => {
                                    const isSelected = a.id === selectedResponse;
                                    const className = classNames("flex flex-col", {
                                        "questions__answer--selected": isSelected,
                                    });
                                    return (
                                        <a
                                            href="#"
                                            key={a.id}
                                            data-id={a.id}
                                            className={className}
                                            onClick={preventDefault(handleSelect)}
                                        >
                                            <div className="flex flex-col">
                                                <OutlinedButton className="check-in__answer-select-button">
                                                    {a.display_number}
                                                </OutlinedButton>
                                            </div>
                                        </a>
                                    );
                                })}
                            </div>
                            {/* If split show textRight only on second group */}
                            {splitAnswers && textRight && agIdx === 1 && (
                                <AnswerText className="text-right" text={textRight} />
                            )}
                            {/* If not split show left/right text along bottom at bounds */}
                            {!splitAnswers && (
                                <div className="flex">
                                    <AnswerText text={textLeft} />
                                    <AnswerText className="flex-grow text-right" text={textRight} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {!showFollowUp && (
                <QuestionButtonGroup
                    handleSkip={handleSkip}
                    isLastQuestion={isLastQuestion}
                    isRequired={isRequired}
                />
            )}
        </>
    );
};

export default React.memo(SelectOneENPS);
